import { FC, forwardRef, Ref, useEffect, useImperativeHandle, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { ID, isNotEmpty } from '../../../../../_metronic/helpers';
import { Client, ClientContact, ClientContactConsent, ClientProvider } from '../core/_models';
import { Provider, Language } from '../../../../helpers/models';
import clsx from 'clsx';
import { useAuth } from '../../../../../app/modules/auth';
import { roleGates } from '../../../auth/roleGates';
import { Phone } from '../../../widgets/components/Phone';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { LanguageSelect } from '../../../widgets/components/LanguageSelect';
import { AddressAutocomplete } from '../../../widgets/components/AddressAutocomplete';
import { LoadingIndicator } from './LoadingIndicator';
import { EditComponentRef } from '../ClientDetailsPage';
import { TimezoneSelect } from '../../../widgets/components/TimezoneSelect';
import { monitoringCaptureError } from '../../../../helpers/monitoring';
import { useAlert } from 'react-alert';
import { values } from 'core-js/core/array';
import { createProvider, updateProvider } from '../../providers/core/_requests';
import { getClientProviderById } from '../../../../helpers/api';
import moment from 'moment';
import {
  createClientContact,
  updateClientContact,
  createClientContactConsent,
  updateClientContactConsent,
  getClientContact,
  deleteClientContact,
  deleteClientContactConsent,
  getClientById,
} from '../core/_requests';
import { Modal, Form } from 'react-bootstrap';
import { ClientProviderSelect } from '../../../widgets/components/ClientProviderSelect';
import { ConfirmDialog } from '../../../widgets/ConfirmDialog';
import { useParams } from 'react-router-dom';
import { scrollToElement } from '../../../../helpers/funcs';

// Define contact roles as a constant
const defaultContactRoles = [
  { value: 'Teacher', label: 'Teacher' },
  { value: 'Counselor', label: 'Counselor' },
  { value: 'Therapist', label: 'Therapist' },
  { value: 'Doctor', label: 'Doctor' },
  { value: 'Provider', label: 'Provider' },
  { value: 'Specialist', label: 'Specialist' },
  { value: 'Educational Therapist', label: 'Educational Therapist' },
  { value: 'Dyslexia Tutor', label: 'Dyslexia Tutor' },
  { value: 'Executive Function Coach', label: 'Executive Function Coach' },
  { value: 'Speech Therapist', label: 'Speech Therapist' },
  { value: 'Occupational Therapist', label: 'Occupational Therapist' },
  { value: 'Physical Therapist', label: 'Physical Therapist' },
  { value: 'Psychologist', label: 'Psychologist' },
  { value: 'Psychiatrist', label: 'Psychiatrist' },
  { value: 'Social Worker', label: 'Social Worker' },
  { value: 'Case Manager', label: 'Case Manager' },
  { value: 'Administrator', label: 'Administrator' },
  { value: 'Other', label: 'Other' },
];

type Props = {
  isLoading: boolean;
  clientContact: ClientContact;
  client: Client;
  clientProvider?: ClientProvider;
  onCancel?: () => void;
  onTouched?: (isValid?: boolean, isDirty?: boolean) => void;
  editMode?: boolean;
  contactIndex?: number;
  onRemoveNew?: (contactIndex: number) => void;
  onEditMode?: (mode: boolean) => void;
  onContactsChange?: (contacts: ClientContact[]) => void;
  noEdit?: boolean;
  consentId?: string;
};

const editContactSchema = Yup.object().shape({
  contact: Yup.object().shape({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
    // phone: Yup.string().required('Phone number is required'),
    timezone: Yup.string().required('Timezone is required'),
    // address: Yup.string().required('Address is required'),
    // zipcode: Yup.string().required('Zipcode is required'),
  }),
});

const ContactInfo = forwardRef((props: Props, ref: Ref<EditComponentRef>) => {
  const { currentUser, currentClient, currentGuardian, currentProvider } = useAuth();
  const { editClient } = roleGates(currentUser, currentProvider); // WST-319: clients.edit
  const [editMode, setEditMode] = useState(props.editMode === true || (props.clientContact.uuid ? false : true));
  const [clientContact, setClientContact] = useState(props.clientContact);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [contactRoles, setContactRoles] = useState(defaultContactRoles);
  const alert = useAlert();
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consent, setConsent] = useState<ClientContactConsent>({
    consent_name: '',
    consent_date: new Date(),
  });

  useEffect(() => {
    setEditMode(props.editMode === true);
  }, [props.editMode]);

  const cancel = () => {
    setClientContact(props.clientContact);
    setEditMode(false);
    formik.resetForm();
    if (props.onCancel) props.onCancel();
  };

  const initialValues = {
    uuid: props.clientContact?.uuid,
    client: props.clientContact?.client || props.client,
    // provider: props.clientProvider?.provider,
    contact: {
      first_name: props.clientContact?.contact?.first_name || '',
      middle_name: props.clientContact?.contact?.middle_name || '',
      last_name: props.clientContact?.contact?.last_name || '',
      email: props.clientContact?.contact?.email || '',
      phone: props.clientContact?.contact?.phone || '',
      pronouns: props.clientContact?.contact?.pronouns || '',
      timezone: props.clientContact?.contact?.timezone || props.client?.timezone || currentUser?.timezone || '',
      address: props.clientContact?.contact?.address || '',
      zipcode: props.clientContact?.contact?.zipcode || '',
      uuid: props.clientContact?.contact?.uuid || undefined,
    },
    // consent_name: props.clientContact?.consent_name || undefined,
    // consent_date: props.clientContact?.consent_date || undefined,
    role: props.clientContact?.role || undefined,
    is_editable: props.clientContact?.is_editable || true,
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: editContactSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.dirty) {
        return;
      }
      setSubmitting(true);
      try {
        let newClientContact: ClientContact | undefined;
        if (isNotEmpty(values.uuid)) {
          if (values.is_editable) {
            newClientContact = await updateClientContact(values, values);
            if (newClientContact) setClientContact(newClientContact);
          } else {
            alert.error('This contact is not editable');
          }
        } else {
          newClientContact = await createClientContact(values);
          if (newClientContact) {
            if (currentProvider) {
              const consent = await createClientContactConsent(newClientContact, {
                provider: currentProvider,
                allow_org: currentProvider.parent_org || currentProvider.is_org ? true : false,
              });
              if (consent) {
                newClientContact.consents = [consent];
              }
            }
            if (newClientContact) setClientContact(newClientContact);
          }
        }
        setSubmitting(false);
        setEditMode(false);
        if (newClientContact?.uuid) {
          setTimeout(() => {
            scrollToElement(`contact-${newClientContact?.uuid}`);
          }, 1000);
        }
      } catch (ex) {
        console.error(ex);
        monitoringCaptureError(ex, { module: 'ContactInfo' });
      }
    },
  });

  useEffect(() => {
    if (!props.editMode) {
      setClientContact(props.clientContact);
    }
  }, [props.clientContact, props.editMode]);

  useEffect(() => {
    props.onTouched && props.onTouched(formik.isValid, formik.dirty);
  }, [formik.touched, formik.isValid, formik.values, formik.dirty]);

  useImperativeHandle(ref, () => ({
    save: formik.submitForm,
    cancel: cancel,
  }));

  const handleRelationshipChange = (selectedOption: any) => {
    formik.setFieldValue('relationship', selectedOption ? selectedOption.value : '');
  };

  const handleConsentSubmit = async () => {
    try {
      if (!clientContact.uuid || !clientContact.client?.uuid) {
        alert.error('Missing required information for release of information');
        return;
      }

      let result: ClientContactConsent | undefined = undefined;
      if (consent.uuid) {
        result = await updateClientContactConsent(consent, consent);
        if (result) {
          setClientContact({
            ...clientContact,
            consents:
              clientContact.consents
                ?.map((c) => (c.uuid === consent.uuid ? result : c))
                .filter((c): c is ClientContactConsent => c !== undefined) || [],
          });
        }
      } else {
        result = await createClientContactConsent(clientContact, consent);
        if (result) {
          setClientContact({
            ...clientContact,
            consents: [...(clientContact.consents || []), result],
          });
        }
      }

      if (result) {
        // Update the local state with the new consent
        setShowConsentModal(false);
        alert.success('Release of information applied successfully');
      } else {
        alert.error('Failed to update release of information');
        monitoringCaptureError(new Error('Failed to update release of information'), { module: 'ContactInfo', result });
      }
    } catch (error) {
      console.error(error);
      monitoringCaptureError(error, { module: 'ContactInfo' });
      alert.error('Failed to add release of information');
    }
  };

  const openConsentModal = (consent?: ClientContactConsent) => {
    const person = currentClient || currentGuardian;
    const fullName = person ? `${person.first_name} ${person.last_name}`.trim() : '';
    if (consent?.consent_name || consent?.consent_date) {
      alert.error('Release of information already provided');
      return;
    }

    setConsent({ ...consent, consent_name: fullName, consent_date: new Date() });
    setShowConsentModal(true);
  };

  const handleDeleteContact = async () => {
    if (!clientContact.uuid || !clientContact.client?.uuid) {
      alert.error('Missing required information to delete contact');
      return;
    }

    const success = await deleteClientContact(clientContact.client.uuid, clientContact.uuid);
    if (success) {
      alert.success('Contact removed successfully');
      props.onContactsChange &&
        props.onContactsChange(props.client.contacts?.filter((c) => c.uuid !== clientContact.uuid) || []);
    } else {
      alert.error('Failed to remove contact');
    }
  };

  const removeConsent = async (consent: ClientContactConsent) => {
    if (!clientContact.uuid || !clientContact.client?.uuid || !consent.uuid) {
      alert.error('Missing required information to remove release of information');
      return;
    }

    ConfirmDialog({
      title: 'Remove Release of Information',
      message: `Are you sure you want to remove the release of information for ${consent.provider?.name}?`,
      confirmButton: 'Remove',
      confirmClassName: 'btn btn-danger btn-sm',
      cancelButton: 'Cancel',
      onProceed: async (proceed) => {
        if (proceed) {
          const success = await deleteClientContactConsent(clientContact, consent);
          if (success) {
            setClientContact({
              ...clientContact,
              consents: clientContact.consents?.filter((c) => c.uuid !== consent.uuid) || [],
            });
            alert.success('Release of information removed successfully');
          } else {
            alert.error('Failed to remove release of information');
          }
        }
      },
    });
  };

  return (
    <>
      <form id="kt_modal_add_contact_form" className="form" onSubmit={formik.handleSubmit} noValidate>
        <div
          className="card shadow-sm mt-auto mb-5"
          id={clientContact.uuid ? `contact-${clientContact.uuid}` : 'contact-new'}
        >
          <div className={clsx("card-header", clientContact.uuid === undefined && 'bg-light-yellow')}>
            <h3 className="card-title">Contact</h3>
            {!props.noEdit && editClient && (
              <div className="card-toolbar">
                {formik.values.uuid && formik.values.is_editable && !editMode ? (
                  <>
                    <div
                      className="btn btn-outline btn-outline-danger btn-sm me-3"
                      onClick={() => {
                        ConfirmDialog({
                          title: 'Remove Contact',
                          message: `Are you sure you want to remove ${clientContact.contact?.name}?`,
                          confirmButton: 'Remove',
                          confirmClassName: 'btn btn-danger btn-sm',
                          cancelButton: 'Cancel',
                          onProceed: (proceed) => {
                            if (proceed) {
                              handleDeleteContact();
                            }
                          },
                        });
                      }}
                    >
                      Remove
                    </div>
                    {!!props.clientContact.contact?.editable && (
                      <div
                        className="btn btn-outline btn-outline-primary btn-sm"
                        onClick={() => props.onEditMode && props.onEditMode(true)}
                      >
                        Edit
                      </div>
                    )}
                  </>
                ) : (
                  formik.values.uuid === undefined && (
                    <button
                      type="reset"
                      onClick={() => {
                        props.onRemoveNew && props.contactIndex !== undefined && props.onRemoveNew(props.contactIndex);
                      }}
                      className="btn btn-sm btn-outline btn-outline-primary"
                      data-kt-contacts-modal-action="cancel"
                      disabled={formik.isSubmitting || props.isLoading}
                    >
                      Remove
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          <div className="card-body">
            <div className="d-flex flex-column me-n7 pe-7">
              <div className="row mt-4 mt-sm-0 mb-4">
                <div className="col-12 col-sm-4 mb-2">
                  <label className="required fw-bold fs-6 mb-2">First Name</label>
                  <input
                    placeholder="First name"
                    {...formik.getFieldProps('contact.first_name')}
                    type="text"
                    name="contact.first_name"
                    className={clsx(
                      'form-control form-control-solid mb-3 mb-lg-0',
                      { 'is-invalid': formik.touched.contact?.first_name && formik.errors.contact?.first_name },
                      {
                        'is-valid': formik.touched.contact?.first_name && !formik.errors.contact?.first_name,
                      }
                    )}
                    autoComplete="off"
                    disabled={formik.isSubmitting || props.isLoading || !editMode}
                  />
                  {formik.touched.contact?.first_name && formik.errors.contact?.first_name && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">{formik.errors.contact?.first_name}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="col-12 col-sm-4 mb-2">
                  <label className="fw-bold fs-6 mb-2">Middle Name</label>
                  <input
                    placeholder="Middle name"
                    {...formik.getFieldProps('contact.middle_name')}
                    type="text"
                    name="contact.middle_name"
                    className={clsx('form-control form-control-solid mb-3 mb-lg-0')}
                    autoComplete="off"
                    disabled={formik.isSubmitting || props.isLoading || !editMode}
                  />
                </div>

                <div className="col-12 col-sm-4 mb-2">
                  <label className="required fw-bold fs-6 mb-2">Last Name</label>
                  <input
                    placeholder="Last name"
                    {...formik.getFieldProps('contact.last_name')}
                    type="text"
                    name="contact.last_name"
                    className={clsx(
                      'form-control form-control-solid mb-3 mb-lg-0',
                      { 'is-invalid': formik.touched.contact?.last_name && formik.errors.contact?.last_name },
                      {
                        'is-valid': formik.touched.contact?.last_name && !formik.errors.contact?.last_name,
                      }
                    )}
                    autoComplete="off"
                    disabled={formik.isSubmitting || props.isLoading || !editMode}
                  />
                  {formik.touched.contact?.last_name && formik.errors.contact?.last_name && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">{formik.errors.contact?.last_name}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-12 col-sm-6 col-xl-4 mb-2">
                  <label className="required fw-bold fs-6 mb-2">Email</label>
                  <input
                    placeholder="Email"
                    {...formik.getFieldProps('contact.email')}
                    className={clsx(
                      'form-control form-control-solid mb-3 mb-lg-0',
                      { 'is-invalid': formik.touched.contact?.email && formik.errors.contact?.email },
                      {
                        'is-valid': formik.touched.contact?.email && !formik.errors.contact?.email,
                      }
                    )}
                    type="email"
                    name="contact.email"
                    autoComplete="off"
                    disabled={formik.isSubmitting || props.isLoading || !editMode}
                  />
                  {formik.touched.contact?.email && formik.errors.contact?.email && (
                    <div className="fv-plugins-message-container">
                      <span role="alert">{formik.errors.contact?.email}</span>
                    </div>
                  )}
                </div>

                <div className="col-12 col-sm-6 col-xl-4 mb-2">
                  <label className="fw-bold fs-6 mb-2">Phone Number</label>
                  <Phone
                    {...formik.getFieldProps('contact.phone')}
                    value={formik.values.contact?.phone}
                    onChange={(val) => formik.setFieldValue('contact.phone', val)}
                    onBlur={formik.handleBlur('contact.phone')}
                    className={clsx(
                      'form-control form-control-solid pt-2 pb-2 mb-3 mb-lg-0',
                      { 'is-invalid': formik.touched.contact?.phone && formik.errors.contact?.phone },
                      {
                        'is-valid': formik.touched.contact?.phone && !formik.errors.contact?.phone,
                      }
                    )}
                    autoComplete={'none'}
                    disabled={formik.isSubmitting || props.isLoading || !editMode}
                  />
                  {formik.touched.contact?.phone && formik.errors.contact?.phone && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">{formik.errors.contact?.phone}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* <div className="col-12 col-sm-6 col-xl-4 mb-2">
                  <label className="fw-bold fs-6 mb-2">Pronouns</label>
                  <input
                    placeholder="Pronouns"
                    {...formik.getFieldProps('contact.pronouns')}
                    type="text"
                    name="contact.pronouns"
                    className={clsx('form-control form-control-solid mb-3 mb-lg-0')}
                    autoComplete="off"
                    disabled={formik.isSubmitting || props.isLoading || !editMode}
                  />
                </div> */}
                <div className="col-12 col-sm-6 col-xl-4 mb-2">
                  <label className="fw-bold fs-6 mb-2">Role</label>
                  <CreatableSelect
                    classNamePrefix="serviceSelect"
                    className={clsx('form-control form-control-solid pt-0 pb-0 mb-3 mb-lg-0 serviceSelect')}
                    value={formik.values.role ? { value: formik.values.role, label: formik.values.role } : null}
                    onChange={(selection) => {
                      if (selection) {
                        formik.setFieldValue('role', selection.value);
                      }
                    }}
                    onCreateOption={(inputValue) => {
                      const newOption = { value: inputValue, label: inputValue };
                      setContactRoles([...contactRoles, newOption]);
                      formik.setFieldValue('role', inputValue);
                    }}
                    options={contactRoles}
                    isDisabled={formik.isSubmitting || props.isLoading || !editMode}
                    placeholder={editMode ? 'Type or select Role' : ''}
                    isClearable
                    isSearchable
                  />
                  {formik.touched.role && formik.errors.role && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">{formik.errors.role}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="col-12 col-xl-6 mb-2">
                  <label className="required fw-bold fs-6 mb-2">Time Zone</label>
                  <TimezoneSelect
                    classNamePrefix="timezoneSelect"
                    className={clsx('form-control form-control-solid pt-0 pb-0 mb-3 mb-lg-0 timezoneSelect')}
                    value={formik.values.contact?.timezone}
                    onChange={(selection) => {
                      if (selection) {
                        formik.setFieldValue('contact.timezone', selection);
                      }
                    }}
                    disabled={formik.isSubmitting || props.isLoading || !editMode}
                  />
                </div>
              </div>

              <div className="row mb-4">
                <div className="col-12 mb-2">
                  <label className="fw-bold fs-6 mb-2">Address</label>
                  <AddressAutocomplete
                    {...formik.getFieldProps('contact.address')}
                    onChange={(address: any) => {
                      formik.setFieldValue('contact.address', address);
                    }}
                    value={formik.values.contact?.address}
                    onChangePostalCode={(postalCode: string) => formik.setFieldValue('contact.zipcode', postalCode)}
                    disabled={formik.isSubmitting || props.isLoading || !editMode}
                    classNamePrefix="address"
                    className={clsx('form-control form-control-solid pt-0 pb-0 mb-3 mb-lg-0 address')}
                  />
                </div>
              </div>
              {!editMode && (
                <div className="row mb-4">
                  <div className="col-12 mb-2">
                    <label className="fw-bold fs-6 mb-2">
                      Release of Information{' '}
                      {(currentClient || currentGuardian) && (
                        <div
                          className="btn btn-outline btn-outline-primary btn-tiny ms-3"
                          onClick={() => openConsentModal()}
                        >
                          <span>Add</span>
                        </div>
                      )}
                    </label>
                    {clientContact.consents && clientContact.consents?.length > 0 ? (
                      <div>
                        {clientContact.consents?.map((consent: ClientContactConsent) => (
                          <div
                            key={consent.uuid}
                            className={props.consentId === consent.uuid ? 'bg-light-yellow p-2 rounded' : ''}
                          >
                            {consent.consent_name && consent.consent_date ? (
                              <>
                                Release of information for {consent.provider?.name} approved by {consent.consent_name}{' '}
                                on {moment(consent.consent_date).format('l')}
                                {(currentClient || currentGuardian) && (
                                  <div
                                    className="btn btn-outline btn-outline-danger btn-tiny ms-3 mt-1 mb-1"
                                    onClick={() => removeConsent(consent)}
                                  >
                                    <span>
                                      <i className="far fa-trash-alt inherit-color ms-1"></i>
                                    </span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <>
                                {currentProvider ? (
                                  <div>Pending release of information approval</div>
                                ) : (
                                  <>
                                    {consent.provider?.name} is awaiting release of information approval
                                    <div
                                      className="btn btn-outline btn-outline-primary btn-tiny ms-3"
                                      onClick={() => openConsentModal(consent)}
                                    >
                                      <span>Approve Release of Information</span>
                                    </div>
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      (currentClient || currentGuardian) && (
                        <div className="text-gray-800">
                          <span className="fw-bolder">This contact is not visible to any providers.</span>
                          <br />
                          Click{' '}
                          <span className="text-primary cursor-pointer" onClick={() => openConsentModal()}>
                            Add
                          </span>{' '}
                          to allow your provider(s) to share information with this contact.
                        </div>
                      )
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Consent Modal */}
        <Modal show={showConsentModal} onHide={() => setShowConsentModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Release of Information</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                {!consent.uuid && (
                  <>
                    <Form.Label>Provider</Form.Label>
                    <ClientProviderSelect
                      provider={consent.provider}
                      onChange={(newProvider) => {
                        let newConsent = { ...consent, provider: newProvider };
                        if (newProvider?.parent_org || newProvider?.is_org) {
                          newConsent.allow_org = true;
                        }
                        setConsent(newConsent);
                      }}
                      placeHolder="Select provider"
                      allowOrg={false}
                      excludeProvider={clientContact.contact}
                    />
                  </>
                )}
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={consent.consent_name}
                  onChange={(e) => setConsent({ ...consent, consent_name: e.target.value })}
                  placeholder="Enter your name"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Date</Form.Label>
                <Form.Control
                  type="date"
                  value={moment(consent.consent_date).format('YYYY-MM-DD')}
                  onChange={(e) => setConsent({ ...consent, consent_date: new Date(e.target.value) })}
                />
              </Form.Group>
            </Form>
            <p className="mt-10">
              I, <span className="fw-bolder">{consent.consent_name}</span>, hereby give release of information consent
              for <span className="fw-bolder">{consent.provider?.name}</span> to share notes and documents, and invite{' '}
              <span className="fw-bolder">{clientContact.contact?.name}</span> to appointments or sessions, as needed.
            </p>
            {(consent.provider?.parent_org || consent.provider?.is_org) && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Approve for all clinic providers"
                  checked={consent.allow_org || false}
                  onChange={(e) => setConsent({ ...consent, allow_org: e.target.checked })}
                />
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button
              className="btn btn-outline btn-outline-primary btn-sm me-3"
              onClick={() => setShowConsentModal(false)}
            >
              Cancel
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleConsentSubmit}>
              Approve Release of Information
            </button>
          </Modal.Footer>
        </Modal>

        {props.isLoading && <LoadingIndicator />}
      </form>
    </>
  );
});

const ClientContactInfo = () => {
  const { clientId, contactId, consentId } = useParams();
  const { currentClient, currentGuardian } = useAuth();
  const [client, setClient] = useState<Client | undefined>(undefined);
  const [clientContact, setClientContact] = useState<ClientContact | undefined>(undefined);
  const [isLoadingClient, setIsLoadingClient] = useState(true);
  const [isLoadingContact, setIsLoadingContact] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (contactId) {
      getClientContact(clientId, contactId)
        .then((clientContact) => {
          setClientContact(clientContact);
          setIsLoadingContact(false);
        })
        .catch((error) => {
          console.error(error);
          setError('Failed to load contact');
          setIsLoadingContact(false);
        });
    }
    if (clientId) {
      getClientById(clientId)
        .then((client) => {
          setClient(client);
          setIsLoadingClient(false);
        })
        .catch((error) => {
          console.error(error);
          setError('Failed to load client');
          setIsLoadingClient(false);
        });
    }
  }, [clientId, contactId, consentId]);

  if (isLoadingClient || isLoadingContact) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <div className="text-danger">{error}</div>;
  }

  if (!client || !clientContact) {
    return <div>No client or contact information available</div>;
  }

  return (
    <div className="clientDetails">
      <ContactInfo
        client={client}
        clientContact={clientContact}
        isLoading={isLoadingClient || isLoadingContact}
        editMode={false}
        noEdit={true}
        consentId={consentId}
      />
    </div>
  );
};

export { ContactInfo, ClientContactInfo };
